import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDemoData } from '../components/useDemoData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Handshake, DollarSign, TrendingUp, TrendingDown, AlertTriangle,
  Calendar, FileText, CheckCircle2, XCircle, Clock,
  ArrowRight, Zap, Target, BarChart3, Shield, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyDataState from '../components/EmptyDataState';

const DEMO_VENDOR = {
  summary: {
    totalContractValue: 3240000,
    annualSavingsOpp: 486000,
    contractsAnalyzed: 4,
    renewalsNext90d: 2,
    overPayPercent: 28,
    avgDiscount: 12,
    benchmarkScore: 62,
  },
  contracts: [
    {
      id: 'c1', provider: 'AWS', type: 'Enterprise Discount Program (EDP)', annualCommit: 1800000,
      currentDiscount: 10, benchmarkDiscount: 18, gapPercent: 8, savingsOpp: 144000,
      expiresIn: '4 months', status: 'renewal-soon', renewalDate: 'Aug 15, 2026',
      issues: ['Below-market discount rate (10% vs 18% benchmark for your spend level)', 'No rollover clause for unused commitment', 'Marketplace spend capped at 20% (industry standard: 30%)'],
      negotiationTips: ['Leverage 22% YoY growth to push for 16-18% EDP discount', 'Request rollover of up to 15% unused commitment', 'Negotiate Marketplace cap increase to 30%', 'Ask for price protection clause (max 5% annual increase)'],
    },
    {
      id: 'c2', provider: 'Azure', type: 'MACC (Microsoft Azure Consumption Commitment)', annualCommit: 720000,
      currentDiscount: 8, benchmarkDiscount: 14, gapPercent: 6, savingsOpp: 43200,
      expiresIn: '14 months', status: 'active', renewalDate: 'Jun 30, 2027',
      issues: ['Ramp structure front-loaded (60/40 split vs recommended 40/60)', 'No Azure Credits for non-consumption services', 'Missing price lock on new services'],
      negotiationTips: ['Restructure ramp to 40/30/30 for better cash flow', 'Include Azure OpenAI credits in MACC', 'Add price lock clause for services adopted mid-term'],
    },
    {
      id: 'c3', provider: 'GCP', type: 'Committed Use Discount (CUD)', annualCommit: 420000,
      currentDiscount: 15, benchmarkDiscount: 20, gapPercent: 5, savingsOpp: 21000,
      expiresIn: '8 months', status: 'active', renewalDate: 'Dec 20, 2026',
      issues: ['Only 1-year CUDs — 3-year would yield deeper discounts', 'No Flex CUDs for variable workloads', 'BigQuery flat-rate not optimized'],
      negotiationTips: ['Switch ML training workloads to 3-year CUDs (additional 20% savings)', 'Add Flex CUDs for burst compute', 'Negotiate BigQuery editions pricing vs flat-rate'],
    },
    {
      id: 'c4', provider: 'Snowflake', type: 'Capacity Contract', annualCommit: 300000,
      currentDiscount: 5, benchmarkDiscount: 18, gapPercent: 13, savingsOpp: 39000,
      expiresIn: '2 months', status: 'renewal-urgent', renewalDate: 'Jun 15, 2026',
      issues: ['Severely below-market discount (5% vs 18% for $300K commit)', 'No auto-suspend on warehouses in contract', 'On-demand pricing for overages (very expensive)'],
      negotiationTips: ['Your spend justifies 15-18% discount — push hard', 'Require auto-suspend clause to prevent idle warehouse costs', 'Cap overage pricing at 1.2x contracted rate', 'Request quarterly true-up vs annual'],
    },
  ],
};

const VendorDealIntel = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedContract, setExpandedContract] = useState(null);

  useEffect(() => {
    // Iter84ak — real backend fetch; rich local demo as fallback.
    let cancelled = false;
    (async () => {
      if (!shouldShowData) { setLoading(false); return; }
      try {
        const token = localStorage.getItem('token');
        await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/finops/vendor-deal-intel/upcoming`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) { setData(DEMO_VENDOR); setLoading(false); }
      } catch (_e) {
        if (!cancelled) { setData(DEMO_VENDOR); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [shouldShowData]);

  if (!shouldShowData) {
    return <EmptyDataState title="Vendor Deal Intelligence" description="AI analyzes your cloud contracts, benchmarks against industry data, and tells you exactly how much you're overpaying — with negotiation playbooks." icon={Handshake} />;
  }

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[400px]"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const statusColor = (s) => ({ 'renewal-urgent': 'bg-red-500/10 text-red-600 border-red-500/30', 'renewal-soon': 'bg-amber-500/10 text-amber-600 border-amber-500/30', active: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' }[s]);
  const statusLabel = (s) => ({ 'renewal-urgent': 'Renewal Urgent', 'renewal-soon': 'Renewal Soon', active: 'Active' }[s]);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="vendor-deal-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
            <Handshake className="w-7 h-7 text-emerald-400" />
            Vendor Deal <span className="text-emerald-400">Intelligence</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">Know exactly what you should be paying. Negotiate with data, not guesses.</p>
        </div>
        <Badge className="bg-red-500/10 text-red-600 border-red-500/30 text-sm px-3 py-1">
          <DollarSign className="w-3.5 h-3.5 mr-1" />${(data.summary.annualSavingsOpp / 1000).toFixed(0)}K/yr negotiable
        </Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-400 mb-1">Total Contract Value</p><p className="text-2xl font-bold text-slate-900">${(data.summary.totalContractValue / 1000000).toFixed(1)}M</p><p className="text-xs text-slate-400">annual commitments</p></CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-4"><p className="text-xs text-red-500 mb-1">You're Overpaying</p><p className="text-2xl font-bold text-red-600">{data.summary.overPayPercent}%</p><p className="text-xs text-red-400">vs market benchmarks</p></CardContent></Card>
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4"><p className="text-xs text-emerald-600 mb-1">Savings Opportunity</p><p className="text-2xl font-bold text-emerald-700">${(data.summary.annualSavingsOpp / 1000).toFixed(0)}K</p><p className="text-xs text-emerald-500">per year if renegotiated</p></CardContent></Card>
        <Card className="border-amber-200"><CardContent className="p-4"><p className="text-xs text-amber-500 mb-1">Renewals in 90 Days</p><p className="text-2xl font-bold text-amber-600">{data.summary.renewalsNext90d}</p><p className="text-xs text-amber-400">action needed</p></CardContent></Card>
      </div>

      <div className="space-y-4">
        {data.contracts.map((c) => (
          <Card key={c.id} className={`cursor-pointer transition-all hover:shadow-md ${expandedContract === c.id ? 'ring-2 ring-indigo-200' : ''}`} onClick={() => setExpandedContract(expandedContract === c.id ? null : c.id)} data-testid={`contract-${c.id}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-center"><p className="text-lg font-bold text-slate-800">{c.provider}</p><p className="text-xs text-slate-400">{c.type}</p></div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center"><p className="text-xs text-slate-400">Your Discount</p><p className="text-lg font-bold text-red-500">{c.currentDiscount}%</p></div>
                  <ArrowRight className="w-4 h-4 text-slate-300" />
                  <div className="text-center"><p className="text-xs text-slate-400">Benchmark</p><p className="text-lg font-bold text-emerald-500">{c.benchmarkDiscount}%</p></div>
                  <div className="text-center"><p className="text-xs text-slate-400">Savings</p><p className="text-lg font-bold text-emerald-600">${(c.savingsOpp / 1000).toFixed(0)}K/yr</p></div>
                  <Badge className={`${statusColor(c.status)} text-xs`}>{statusLabel(c.status)}</Badge>
                </div>
              </div>

              {expandedContract === c.id && (
                <div className="mt-5 pt-5 border-t border-slate-200 grid lg:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-1"><XCircle className="w-4 h-4" />Issues Found</p>
                    <ul className="space-y-2">{c.issues.map((issue, i) => <li key={i} className="text-sm text-slate-400 flex items-start gap-2"><span className="text-red-400 mt-0.5 shrink-0">&#x2022;</span>{issue}</li>)}</ul>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-600 mb-2 flex items-center gap-1"><Zap className="w-4 h-4" />Negotiation Playbook</p>
                    <ul className="space-y-2">{c.negotiationTips.map((tip, i) => <li key={i} className="text-sm text-slate-400 flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{tip}</li>)}</ul>
                  </div>
                  <div className="lg:col-span-2 flex items-center gap-4 pt-2">
                    <div className="flex items-center gap-2 text-sm text-slate-400"><Calendar className="w-4 h-4" />Renewal: {c.renewalDate}</div>
                    <div className="flex items-center gap-2 text-sm text-slate-400"><Clock className="w-4 h-4" />Expires in {c.expiresIn}</div>
                    <div className="flex items-center gap-2 text-sm text-slate-400"><DollarSign className="w-4 h-4" />Annual commit: ${(c.annualCommit / 1000).toFixed(0)}K</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default VendorDealIntel;
